import { Activity, ProfileData, Settings } from '../model/Model';
import create, { State } from 'zustand';
import { getActivities, resolveStyle, resolveToken } from '../service/rest';
import { CheckinForm } from '../service/checkin-flow';
import { checkInFunc, checkoutFunc, fetchFunc, searchFunc, SearchVisitorState } from './checkin-actions';

export interface IActivityState extends State {
    loaded: boolean;
    loading: boolean;
    activities?: Activity[];
    fetch: (token: string, typeId: number | undefined) => void;
}

export interface ICheckinState extends State {
    loaded: boolean;
    loading: boolean;
    activeCheckin?: {
        workerId?: number;
        checkinTime?: string;
        profile?: ProfileData;
    },
    lastCheckin?: {
        workerId?: number;
        checkinTime?: string;
        checkoutTime?: string;
        profile?: ProfileData;
    },
    fetch: (token: string) => void;
    search: (token: string, profile: ProfileData, rememberMe: boolean, callback: (state: SearchVisitorState) => void) => void;
    checkIn: (token: string, checkinForm: CheckinForm, callback: (success: boolean) => void) => void;
    checkOut: (token: string) => void;
    reset: () => void;
}

interface IReportState extends State {
    error: boolean;
    message?: string;
    reportError: (message: string) => void;
    reportSuccess: (message: string) => void;
    reset: () => void;
}

interface ISiteState extends State {
    loaded: boolean;
    name?: string;
    timezone?: string;
    declaration?: string;
    rules?: string;
    settings?: Settings;
    fetch: (token: string) => void;
}

interface IStyleState extends State {
    loaded: boolean;
    background?: string;
    logo?: string;
    style?: string;
    linkColor?: string;
    headerDesign?: number;
    fetch: (token: string) => void;
}

export const useCheckinState = create<ICheckinState>((set, get) => ({
    loaded: false,
    loading: false,
    search: searchFunc(set),
    checkIn: checkInFunc(set),
    checkOut: checkoutFunc(set, get),
    fetch: fetchFunc(set),
    reset: () => {
        set({
            loaded: false,
            loading: false,
            activeCheckin: {
                workerId: undefined,
                checkinTime: undefined,
                profile: undefined
            },
            lastCheckin: {
                workerId: undefined,
                checkinTime: undefined,
                checkoutTime: undefined,
                profile: undefined
            }
        });
    }
}));

export const useActivityState = create<IActivityState>(set => ({
    loaded: false,
    loading: false,
    fetch: async (token: string, typeId: number | undefined) => {
        try {
            set({
                loading: true,
                loaded: false
            })
            const activities = await getActivities(token, typeId);
            set({
                loaded: true,
                activities: activities
            })
        } catch (e) {
            const { getState } = useReportState;
            getState().reportError('Network error while fetching activities, please try again later');
        } finally {
            set({
                loading: false
            })
        }
    }
}));

export const useStyleStore = create<IStyleState>(set => ({
    loaded: false,
    fetch: async (token: string) => {
        try {
            const style = await resolveStyle(token);
            set({
                loaded: true,
                ...style,
            });
        } catch (e) {
            const { getState } = useReportState;
            getState().reportError('Network error while fetching site config, please try again later');
        }
    },
}));

export const useReportState = create<IReportState>(set => ({
    error: false,
    reportSuccess: (message: string) => {
        if (message && message.length > 1) {
            set({
                error: false,
                message: message,
            });
        } else {
            set({
                error: false,
                message: undefined,
            });
        }
    },
    reportError: (message: string) => {
        if (message && message.length > 1) {
            set({
                error: true,
                message: message,
            });
        } else {
            set({
                error: false,
                message: undefined,
            });
        }
    },
    reset: () => {
        set({
            message: undefined,
            error: false,
        });
    },
}));

export const useSiteState = create<ISiteState>(set => ({
    loaded: false,
    fetch: async (token: string) => {
        try {
            const site = await resolveToken(token);
            set({
                loaded: true,
                ...site,
            });
        } catch (e) {
            const { getState } = useReportState;
            getState().reportError('Network error while fetching site config, please try again later');
        }
    },
}));
